import {
  createTextId,
  type InlineTextStyle,
  type TextBlock,
  type TextBlockType,
  type TextDocument,
  type TextRun,
  type TextSelection,
  type TextSelectionPosition,
} from '../model/textDocument';
import { normalizeTextDocument } from '../model/textNormalize';
import {
  endPositionInBlock,
  flatOffsetInBlock,
  positionFromFlatOffset,
  selectionIsCollapsed,
} from './textSelectionModel';

function blockIndexById(document: TextDocument, blockId: string): number {
  return document.blocks.findIndex((block) => block.id === blockId);
}

function runIndexById(block: TextBlock, runId: string): number {
  if (!runId) return block.runs.length === 0 ? 0 : block.runs.length - 1;
  const index = block.runs.findIndex((run) => run.id === runId);
  return index >= 0 ? index : Math.max(0, block.runs.length - 1);
}

function isBeforeOrEqual(document: TextDocument, a: TextSelectionPosition, b: TextSelectionPosition): boolean {
  const blockA = blockIndexById(document, a.blockId);
  const blockB = blockIndexById(document, b.blockId);
  if (blockA !== blockB) return blockA < blockB;
  const block = document.blocks[blockA];
  if (!block) return true;
  const runA = runIndexById(block, a.runId);
  const runB = runIndexById(block, b.runId);
  if (runA !== runB) return runA < runB;
  return a.offset <= b.offset;
}

function orderedSelection(document: TextDocument, selection: TextSelection): [TextSelectionPosition, TextSelectionPosition] {
  return isBeforeOrEqual(document, selection.anchor, selection.focus)
    ? [selection.anchor, selection.focus]
    : [selection.focus, selection.anchor];
}

function splitRun(run: TextRun, start: number, end: number, stylePatch?: InlineTextStyle): TextRun[] {
  const runs: TextRun[] = [];
  const before = run.text.slice(0, start);
  const middle = run.text.slice(start, end);
  const after = run.text.slice(end);
  if (before) runs.push({ ...run, id: createTextId('run'), text: before });
  if (middle) {
    runs.push({
      id: run.id,
      text: middle,
      style: stylePatch ? { ...(run.style ?? {}), ...stylePatch } : run.style,
    });
  }
  if (after) runs.push({ ...run, id: createTextId('run'), text: after });
  return runs;
}

/** 在块内 flat 偏移处拆成「光标前 / 光标后」两段 runs。 */
function splitRunsAtFlatOffset(runs: TextRun[], flatOffset: number): { before: TextRun[]; after: TextRun[] } {
  const clamped = Math.max(0, Math.min(flatOffset, runs.reduce((n, r) => n + r.text.length, 0)));
  const before: TextRun[] = [];
  const after: TextRun[] = [];
  let pos = 0;

  for (const run of runs) {
    const runLen = run.text.length;
    if (pos + runLen <= clamped) {
      before.push(run);
      pos += runLen;
      continue;
    }
    if (pos >= clamped) {
      after.push(run);
      continue;
    }
    const local = clamped - pos;
    const parts = splitRun(run, local, local);
    if (parts[0]) before.push(parts[0]);
    const tail = parts.length > 1 ? parts[parts.length - 1] : null;
    if (tail && tail !== parts[0]) after.push(tail);
    pos += runLen;
  }

  return { before, after };
}

function nextBlockTypeAfterBreak(currentType: TextBlockType): TextBlockType {
  return currentType;
}

export function insertBlockAfterSelection(
  document: TextDocument,
  selection: TextSelection | null,
  fallbackType: TextBlockType,
): { document: TextDocument; selection: TextSelection } {
  const fallbackBlock = document.blocks[document.blocks.length - 1];
  const focusBlockId = selection?.focus.blockId ?? fallbackBlock?.id;
  const focusIndex = focusBlockId ? blockIndexById(document, focusBlockId) : -1;
  const focusBlock = focusIndex >= 0 ? document.blocks[focusIndex]! : fallbackBlock;
  if (!focusBlock) {
    const nextBlock: TextBlock = {
      id: createTextId('block'),
      type: fallbackType,
      runs: [],
    };
    const nextPosition: TextSelectionPosition = { blockId: nextBlock.id, runId: '', offset: 0 };
    return {
      document: normalizeTextDocument({ version: 1, blocks: [nextBlock] }),
      selection: { anchor: nextPosition, focus: nextPosition },
    };
  }

  const nextType = nextBlockTypeAfterBreak(focusBlock.type ?? fallbackType);
  const nextBlockId = createTextId('block');
  const nextPosition: TextSelectionPosition = { blockId: nextBlockId, runId: '', offset: 0 };

  if (!selection || selectionIsCollapsed(selection)) {
    const caret = selection?.focus ?? endPositionInBlock(focusBlock);
    const splitAt = flatOffsetInBlock(focusBlock, caret);
    const { before, after } = splitRunsAtFlatOffset(focusBlock.runs, splitAt);
    const blocks = [...document.blocks];
    blocks[focusIndex] = { ...focusBlock, runs: before };
    blocks.splice(focusIndex + 1, 0, {
      id: nextBlockId,
      type: nextType,
      runs: after,
    });

    // List items at row start: cursor stays in the now-empty original item so that
    // pressing Enter "adds an empty item" where the cursor is, pushing content below.
    // Only applies when content actually fell to the new block (after is non-empty).
    const isListItem =
      focusBlock.type === 'ordered-list-item' || focusBlock.type === 'unordered-list-item';
    const cursorPosition =
      isListItem && splitAt === 0 && after.length > 0
        ? ({ blockId: focusBlock.id, runId: '', offset: 0 } as const)
        : nextPosition;

    return {
      document: normalizeTextDocument({ version: 1, blocks }),
      selection: { anchor: cursorPosition, focus: cursorPosition },
    };
  }

  const [start, end] = orderedSelection(document, selection);
  const startIndex = blockIndexById(document, start.blockId);
  const endIndex = blockIndexById(document, end.blockId);
  if (startIndex < 0 || endIndex < 0) {
    return insertBlockAfterSelection(document, { anchor: selection.focus, focus: selection.focus }, fallbackType);
  }

  const startBlock = document.blocks[startIndex]!;
  const endBlock = document.blocks[endIndex]!;
  const { before } = splitRunsAtFlatOffset(startBlock.runs, flatOffsetInBlock(startBlock, start));
  const { after } = splitRunsAtFlatOffset(endBlock.runs, flatOffsetInBlock(endBlock, end));

  const blocks = [...document.blocks];
  blocks[startIndex] = { ...startBlock, runs: before };
  blocks.splice(startIndex + 1, endIndex - startIndex, { id: nextBlockId, type: nextType, runs: after });

  return {
    document: normalizeTextDocument({ version: 1, blocks }),
    selection: { anchor: nextPosition, focus: nextPosition },
  };
}

function mergePasteRuns(
  before: TextRun[],
  pasted: TextBlock[],
  after: TextRun[],
): TextBlock[] {
  if (!pasted.length) {
    return [{ id: createTextId('block'), type: 'paragraph', runs: [...before, ...after] }];
  }
  if (pasted.length === 1) {
    const only = pasted[0]!;
    return [{
      id: createTextId('block'),
      type: only.type,
      runs: [...before, ...only.runs, ...after],
    }];
  }
  const first = pasted[0]!;
  const last = pasted[pasted.length - 1]!;
  return [
    { id: createTextId('block'), type: first.type, runs: [...before, ...first.runs] },
    ...pasted.slice(1, -1),
    { id: createTextId('block'), type: last.type, runs: [...last.runs, ...after] },
  ];
}

function stripBlockStyles(blocks: TextBlock[]): TextBlock[] {
  return blocks.map((block) => ({
    ...block,
    id: createTextId('block'),
    runs: block.runs.map((run) => ({
      id: createTextId('run'),
      text: run.text,
    })),
  }));
}

export function replaceSelectionWithPastedBlocks(
  document: TextDocument,
  selection: TextSelection | null,
  pastedBlocks: TextBlock[],
): { document: TextDocument; selection: TextSelection } {
  const incoming = stripBlockStyles(pastedBlocks);
  if (!incoming.length) {
    return {
      document,
      selection: selection ?? { anchor: { blockId: '', runId: '', offset: 0 }, focus: { blockId: '', runId: '', offset: 0 } },
    };
  }

  const fallbackBlock = document.blocks[document.blocks.length - 1];
  if (!fallbackBlock) {
    const last = incoming[incoming.length - 1]!;
    const pos = endPositionInBlock(last);
    return {
      document: normalizeTextDocument({ version: 1, blocks: incoming }),
      selection: { anchor: pos, focus: pos },
    };
  }

  if (!selection) {
    const blocks = [...document.blocks, ...incoming];
    const last = blocks[blocks.length - 1]!;
    const pos = endPositionInBlock(last);
    return {
      document: normalizeTextDocument({ version: 1, blocks }),
      selection: { anchor: pos, focus: pos },
    };
  }

  if (!selectionIsCollapsed(selection)) {
    const [start, end] = orderedSelection(document, selection);
    const startIndex = blockIndexById(document, start.blockId);
    const endIndex = blockIndexById(document, end.blockId);
    if (startIndex < 0 || endIndex < 0) {
      return replaceSelectionWithPastedBlocks(document, { anchor: selection.focus, focus: selection.focus }, pastedBlocks);
    }
    const startBlock = document.blocks[startIndex]!;
    const endBlock = document.blocks[endIndex]!;
    const { before } = splitRunsAtFlatOffset(startBlock.runs, flatOffsetInBlock(startBlock, start));
    const { after } = splitRunsAtFlatOffset(endBlock.runs, flatOffsetInBlock(endBlock, end));
    const merged = mergePasteRuns(before, incoming, after);
    const blocks = [
      ...document.blocks.slice(0, startIndex),
      ...merged,
      ...document.blocks.slice(endIndex + 1),
    ];
    const last = merged[merged.length - 1]!;
    const pos = endPositionInBlock(last);
    return {
      document: normalizeTextDocument({ version: 1, blocks }),
      selection: { anchor: pos, focus: pos },
    };
  }

  const focusIndex = blockIndexById(document, selection.focus.blockId);
  const focusBlock = focusIndex >= 0 ? document.blocks[focusIndex]! : fallbackBlock;
  const splitAt = flatOffsetInBlock(focusBlock, selection.focus);
  const { before, after } = splitRunsAtFlatOffset(focusBlock.runs, splitAt);
  const merged = mergePasteRuns(before, incoming, after);
  const blocks = [
    ...document.blocks.slice(0, focusIndex),
    ...merged,
    ...document.blocks.slice(focusIndex + 1),
  ];
  const last = merged[merged.length - 1]!;
  const pos = endPositionInBlock(last);
  return {
    document: normalizeTextDocument({ version: 1, blocks }),
    selection: { anchor: pos, focus: pos },
  };
}

export function applyInlineStyleToSelection(
  document: TextDocument,
  selection: TextSelection | null,
  stylePatch: InlineTextStyle,
): { document: TextDocument; selection: TextSelection | null } {
  if (!selection || selectionIsCollapsed(selection)) return { document, selection };
  const [start, end] = orderedSelection(document, selection);

  const startBlock = document.blocks.find((block) => block.id === start.blockId);
  const endBlock = document.blocks.find((block) => block.id === end.blockId);
  if (!startBlock || !endBlock) return { document, selection };

  const anchorFlat = flatOffsetInBlock(startBlock, start);
  const focusFlat = flatOffsetInBlock(endBlock, end);

  const startBlockIndex = blockIndexById(document, start.blockId);
  const endBlockIndex = blockIndexById(document, end.blockId);
  if (startBlockIndex < 0 || endBlockIndex < 0) return { document, selection };

  const blocks = document.blocks.map((block, blockIndex) => {
    if (blockIndex < startBlockIndex || blockIndex > endBlockIndex) return block;
    const runs: TextRun[] = [];
    block.runs.forEach((run, runIndex) => {
      const startRunIndex = block.id === start.blockId ? runIndexById(block, start.runId) : 0;
      const endRunIndex = block.id === end.blockId ? runIndexById(block, end.runId) : block.runs.length - 1;
      if (runIndex < startRunIndex || runIndex > endRunIndex) {
        runs.push(run);
        return;
      }
      const from = block.id === start.blockId && runIndex === startRunIndex ? start.offset : 0;
      const to = block.id === end.blockId && runIndex === endRunIndex ? end.offset : run.text.length;
      runs.push(...splitRun(run, from, to, stylePatch));
    });
    return { ...block, runs };
  });

  const nextDocument = normalizeTextDocument({ version: 1, blocks });
  const nextStartBlock = nextDocument.blocks.find((block) => block.id === start.blockId);
  const nextEndBlock = nextDocument.blocks.find((block) => block.id === end.blockId);
  if (!nextStartBlock || !nextEndBlock) {
    return { document: nextDocument, selection };
  }

  return {
    document: nextDocument,
    selection: {
      anchor: positionFromFlatOffset(nextStartBlock, anchorFlat),
      focus: positionFromFlatOffset(nextEndBlock, focusFlat),
    },
  };
}

/**
 * Handle Backspace at the very start of a block (collapsed selection, flat-offset 0).
 * Merges the current block's runs into the preceding block and places the cursor at the
 * join point.  Returns null when the block is already the first one (nothing to merge into).
 */
export function deleteBlockBackward(
  document: TextDocument,
  selection: TextSelection,
): { document: TextDocument; selection: TextSelection } | null {
  const focusIndex = blockIndexById(document, selection.focus.blockId);
  if (focusIndex <= 0) return null;

  const prevBlock = document.blocks[focusIndex - 1]!;
  const focusBlock = document.blocks[focusIndex]!;
  const prevPlainLength = prevBlock.runs.reduce((n, r) => n + r.text.length, 0);

  const mergedBlock: TextBlock = {
    ...prevBlock,
    runs: [...prevBlock.runs, ...focusBlock.runs],
  };

  const blocks = [...document.blocks];
  blocks[focusIndex - 1] = mergedBlock;
  blocks.splice(focusIndex, 1);

  const normalizedDoc = normalizeTextDocument({ version: 1, blocks });
  // Use the normalized block (same index) so that positionFromFlatOffset sees correct run IDs.
  const normalizedMerged = normalizedDoc.blocks[focusIndex - 1];
  const cursorPosition = normalizedMerged
    ? positionFromFlatOffset(normalizedMerged, prevPlainLength)
    : { blockId: prevBlock.id, runId: '', offset: 0 };

  return {
    document: normalizedDoc,
    selection: { anchor: cursorPosition, focus: cursorPosition },
  };
}

export function collapsedAtBlockEnd(block: TextBlock): TextSelection {
  const position = endPositionInBlock(block);
  return { anchor: position, focus: position };
}

/**
 * Returns the merged InlineTextStyle that is "active" across the entire selection.
 *
 * A boolean style (bold, italic, …) is considered active only when ALL runs
 * in the selection carry it; this matches standard word-processor toggle behaviour
 * (clicking Bold on a mixed selection makes everything bold on the first press).
 *
 * Value styles (color, backgroundColor, fontSize) are returned only when every
 * run in the selection shares the same value; otherwise the key is omitted.
 */
export function getActiveStylesAtSelection(
  document: TextDocument,
  selection: TextSelection | null,
): InlineTextStyle {
  if (!selection || selectionIsCollapsed(selection)) return {};
  const [start, end] = orderedSelection(document, selection);

  const startBlockIndex = blockIndexById(document, start.blockId);
  const endBlockIndex = blockIndexById(document, end.blockId);
  if (startBlockIndex < 0 || endBlockIndex < 0) return {};

  const coveredRuns: TextRun[] = [];
  for (let bi = startBlockIndex; bi <= endBlockIndex; bi++) {
    const block = document.blocks[bi];
    if (!block) continue;
    const startRun = block.id === start.blockId ? runIndexById(block, start.runId) : 0;
    const endRun = block.id === end.blockId ? runIndexById(block, end.runId) : block.runs.length - 1;
    for (let ri = startRun; ri <= endRun; ri++) {
      const run = block.runs[ri];
      if (run) coveredRuns.push(run);
    }
  }

  if (coveredRuns.length === 0) return {};

  const active: InlineTextStyle = {};
  if (coveredRuns.every((r) => r.style?.bold)) active.bold = true;
  if (coveredRuns.every((r) => r.style?.italic)) active.italic = true;
  if (coveredRuns.every((r) => r.style?.underline)) active.underline = true;
  if (coveredRuns.every((r) => r.style?.strike)) active.strike = true;
  if (coveredRuns.every((r) => r.style?.superscript)) active.superscript = true;
  if (coveredRuns.every((r) => r.style?.subscript)) active.subscript = true;

  const firstColor = coveredRuns[0]?.style?.color;
  if (firstColor && coveredRuns.every((r) => r.style?.color === firstColor)) active.color = firstColor;

  const firstBg = coveredRuns[0]?.style?.backgroundColor;
  if (firstBg && coveredRuns.every((r) => r.style?.backgroundColor === firstBg)) active.backgroundColor = firstBg;

  const firstSize = coveredRuns[0]?.style?.fontSize;
  if (firstSize && coveredRuns.every((r) => r.style?.fontSize === firstSize)) active.fontSize = firstSize;

  return active;
}

